import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  Typography,
  Divider,
  Button,
  Box,
  Chip,
  Alert
} from '@mui/material';
import {
  NotificationsOutlined,
  NotificationsActive,
  Warning,
  Info,
  CheckCircle,
  Error,
  Close,
  Build,
  EventNoteOutlined
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ko, ja, zhCN, zhTW, vi, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import notificationService from '@plannel/services/system/notification-service';
import dateUtil from '@plannel/utils/date-util';
import restApi from '@plannel/services/utils/rest-api';
import { FS } from "@plannel/utils/font-util";
import { MEETING_AGENDA_MENU_KEY, requestAgenda } from "@plannel/pages/collaboration/agendaDeepLink";

/**
 * 안건 알림이면 그 안건 id, 아니면 null.
 *
 * <p>서버가 {@code desc_txt} 에 {@code meetingAgenda|||123} 으로 실어 보낸다 — 파일 다운로드
 * 알림이 이미 쓰고 있는 {@code |||} 규약이다. <b>이 접두어가 아닌 알림은 지금까지와 똑같이
 * 동작한다</b>(알림함은 여러 기능이 같이 쓴다).
 */
const agendaIdOf = (notification) => {
  const parts = String(notification?.description ?? notification?.descTxt ?? '').split('|||');
  return parts[0] === 'meetingAgenda' && /^[0-9]{1,19}$/.test(parts[1] ?? '') ? parts[1] : null;
};

const NotificationBell = () => {
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  // maintenanceAlert: type='maintenance' 인 점검 진행중 배너 (상단 배너에 표시)
  const [maintenanceAlert, setMaintenanceAlert] = useState(null);
  const [showAllNotifications, setShowAllNotifications] = useState(false);
  const [displayLimit] = useState(5);
  // 폴링 간격: 평상시 5분, export 후 5초
  const [pollingInterval, setPollingInterval] = useState(300000);
  // 빠른 폴링 중 새 알림 감지를 위한 기준값
  const prevUnreadCountRef = useRef(0);
  // 이벤트 핸들러에서 클로저 없이 최신 unreadCount 참조용
  const unreadCountRef = useRef(0);
  // useCallback 내부에서 setPollingInterval 참조용 (stale closure 방지)
  const setPollingIntervalRef = useRef(null);

  // 현재 언어에 맞는 date-fns locale 가져오기
  const getDateLocale = () => {
    switch (i18n.language) {
      case 'ko-KR': return ko;
      case 'ja-JP': return ja;
      case 'zh-CN': return zhCN;
      case 'zh-TW': return zhTW;
      case 'vi-VN': return vi;
      case 'en-US':
      default: return enUS;
    }
  };

  // 알림 벨 목록 로드
  // maintenance-notice (점검/릴리즈 사전 안내), maintenance, complete 등 모두 포함
  const loadNotifications = useCallback(async () => {
    try {
      const response = await notificationService.getNotifications();
      const notificationList = response?.data?.data?.results || response?.data?.results || [];

      setNotifications(Array.isArray(notificationList) ? notificationList : []);

      const unread = Array.isArray(notificationList)
        ? notificationList.filter(n => !n.readFlg).length
        : 0;
      setUnreadCount(unread);
      unreadCountRef.current = unread;
    } catch (error) {
      console.error('Failed to load notifications:', error);
      setNotifications([]);
      setUnreadCount(0);
      // 빠른 폴링 중 에러 발생 시 즉시 5분 폴링으로 복귀 (에러 폭주 방지)
      if (window._exportPollingTimeout) {
        clearTimeout(window._exportPollingTimeout);
        window._exportPollingTimeout = null;
      }
      if (setPollingIntervalRef.current) {
        setPollingIntervalRef.current(300000);
      }
    }
  }, []);

  // 상단 배너 로드: type='maintenance' 이면서 점검 시간 범위 내인 경우에만 표시
  // maintenance-notice(사전 안내)는 배너에 표시하지 않음 — 알림 벨 목록에만 포함
  const loadMaintenanceAlert = useCallback(async () => {
    try {
      const response = await notificationService.getMaintenanceNotification();
      const rawAlert = response?.data?.data || response?.data || null;

      if (rawAlert && rawAlert.activeFlg && rawAlert.type === 'maintenance') {
        setMaintenanceAlert({
          ...rawAlert,
          displayTitle: rawAlert.title || 'NOTI_MaintenanceInProgressTitle',
          displayMessage: rawAlert.msgTxt || 'NOTI_MaintenanceInProgressMessage'
        });
      } else {
        setMaintenanceAlert(null);
      }
    } catch (error) {
      console.error('Failed to load maintenance alert:', error);
      setMaintenanceAlert(null);
    }
  }, []);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setShowAllNotifications(false); // 팝오버 열 때마다 더보기 상태 초기화
  };

  const handleClose = () => {
    setAnchorEl(null);
    setShowAllNotifications(false); // 팝오버 닫을 때도 초기화
  };

  const getDisplayNotifications = () => {
    if (!Array.isArray(notifications)) return [];

    // Maintenance 관련 타입 알림을 가장 위로 정렬
    const sortedNotifications = [...notifications].sort((a, b) => {
      const aIsMaintenance = ['maintenance', 'maintenance-notice'].includes(a.type);
      const bIsMaintenance = ['maintenance', 'maintenance-notice'].includes(b.type);

      if (aIsMaintenance && !bIsMaintenance) return -1;
      if (!aIsMaintenance && bIsMaintenance) return 1;
      return 0;
    });

    if (showAllNotifications) {
      return sortedNotifications;
    } else {
      return sortedNotifications.slice(0, displayLimit);
    }
  };

  const displayNotifications = getDisplayNotifications();
  const hasMoreNotifications = notifications.length > displayLimit;

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationService.markNotificationAsRead(notificationId);

      // 알림 목록 다시 로드 (읽음 상태와 읽지 않은 개수 함께 업데이트)
      await loadNotifications();
    } catch (error) {
      // 알림 읽음 처리 실패
    }
  };

  const handleDeleteNotification = async (notificationId, event) => {
    // 이벤트 전파 방지 (ListItem 클릭 이벤트와 충돌 방지)
    event.stopPropagation();

    try {
      // hideNotificationForUser를 호출하여 z_notification_read_status 테이블의 hidden을 true로 설정
      await notificationService.hideNotificationForUser(notificationId);

      // 숨김 처리 후 알림 목록 다시 로드 (읽지 않은 개수도 함께 계산됨)
      await loadNotifications();
    } catch (error) {
      // 알림 숨김 처리 실패
      console.warn('Failed to hide notification:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllNotificationsAsRead();

      // 로컬 상태 업데이트
      setNotifications(prev =>
        prev.map(notification => ({ ...notification, readFlg: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      // 전체 알림 읽음 처리 실패
    }
  };

  /*
   * 알림 제목별 아이콘.
   *
   * type 은 심각도(info/warning/error)라서 "무슨 일인지" 는 못 알려 준다 — 회의 안건 배정과
   * 일반 안내가 똑같은 i 아이콘으로 나오면 목록에서 구분이 안 된다. 제목 키를 가진 알림은
   * 그 기능의 아이콘을 쓰고, 화면(메뉴)에서 쓰는 것과 같은 아이콘으로 맞춘다.
   *
   * 새 기능이 알림을 추가할 때 여기 한 줄만 더하면 된다. 없으면 아래 type 규칙 그대로다.
   */
  const TITLE_ICONS = {
    NOTI_MeetingAgendaAssignedTitle: <EventNoteOutlined fontSize="small" color="primary" />,
    // 급한 안건은 목록에서 색으로 먼저 걸린다 — 제목을 읽기 전에 보인다
    NOTI_MeetingAgendaAssignedUrgentTitle: <EventNoteOutlined fontSize="small" sx={{ color: '#A81F1F' }} />
  };

  /**
   * 알림 목록 아이콘 반환
   * maintenance-notice 타입은 title 종류에 관계없이 Build 아이콘으로 통일
   */
  const getNotificationIcon = (type, title) => {
    if (TITLE_ICONS[title]) {
      return TITLE_ICONS[title];
    }
    switch (type) {
      case 'maintenance':
      case 'maintenance-notice':
        return <Build fontSize="small" sx={{ color: 'warning.main' }} />;
      case 'system':
      case 'success':
        return <CheckCircle color="primary" />;
      case 'warning':
        return <Warning color="warning" />;
      case 'error':
        return <Error color="error" />;
      case 'info':
        return <Info color="info" />;
      default:
        return <Info color="action" />;
    }
  };

  const formatDateTime = (dateTime, notificationType = null, startTime = null) => {
    // dateTime이 null, undefined, 또는 빈 문자열인 경우 처리
    if (!dateTime) {
      return t('noDateSpecified');
    }

    try {
      let targetTime = dateTime;

      // maintenance 타입인 경우 startTime을 기준으로 계산
      if (notificationType === 'maintenance' && startTime) {
        targetTime = startTime;
      }

      // dateUtil의 formatDateTime을 사용하여 UTC → 브라우저 타임존 변환
      const formattedDateTime = dateUtil.formatDateTime(targetTime);

      if (!formattedDateTime) {
        return t('invalidDate');
      }

      // 포맷된 시간을 Date 객체로 변환하여 상대 시간 계산
      const date = new Date(targetTime);
      const now = new Date();
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));

      if (diffInMinutes < 1) {
        return t('justNow');
      } else if (diffInMinutes < 60) {
        return t('minutesAgo', { count: diffInMinutes });
      } else if (diffInMinutes < 24 * 60) {
        const hours = Math.floor(diffInMinutes / 60);
        return t('hoursAgo', { count: hours });
      } else {
        // 24시간 이상인 경우 절대 시간으로 표시 (사용자의 로컬 시간대)
        const locale = getDateLocale();
        return format(date, 'MM/dd HH:mm', { locale });
      }
    } catch (error) {
      return t('dateError');
    }
  };

  const formatMaintenanceDateTime = (dateTime) => {
    if (!dateTime) {
      return t('noDateSpecified');
    }

    try {
      // dateUtil을 사용하여 UTC → 브라우저 타임존 변환
      if (typeof dateTime === 'string') {
        // 로컬 시간대로 변환
        const date = new Date(dateTime);

        // "2025-11-05 11:30:00" 형태의 문자열을 직접 Date로 변환
        if (isNaN(date.getTime())) {
          return t('invalidDate');
        }

        // 언어별 날짜 형식
        const locale = getDateLocale();
        switch (i18n.language) {
          case 'ko-KR':
            return format(date, 'MM월 dd일 HH:mm', { locale });
          case 'ja-JP':
            return format(date, 'MM月dd日 HH:mm', { locale });
          case 'zh-CN':
          case 'zh-TW':
            return format(date, 'MM月dd日 HH:mm', { locale });
          case 'vi-VN':
            return format(date, 'dd/MM HH:mm', { locale });
          case 'en-US':
          default:
            return format(date, 'MMM dd HH:mm', { locale });
        }
      }

      // 파싱에 실패한 경우 에러 반환
      return t('dateError');
    } catch (error) {
      console.warn('Date parsing error:', error, 'Input:', dateTime);
      return t('dateError');
    }
  };

  const formatMaintenanceDateShort = (dateTime) => {
    if (!dateTime) return '';
    try {
      const date = new Date(dateTime);
      if (isNaN(date.getTime())) return '';
      return format(date, 'MM/dd');
    } catch (error) {
      return '';
    }
  };

  const formatMaintenanceTimeOnly = (dateTime) => {
    if (!dateTime) return '';
    try {
      const date = new Date(dateTime);
      if (isNaN(date.getTime())) return '';
      return format(date, 'HH:mm');
    } catch (error) {
      return '';
    }
  };

  const open = Boolean(anchorEl);
  // setPollingInterval을 ref에 동기화 (useCallback 내부 stale closure 방지)
  setPollingIntervalRef.current = setPollingInterval;

  useEffect(() => {
    // 초기 로드
    loadNotifications();
    loadMaintenanceAlert();

    // 5분마다 알림 확인
    const interval = setInterval(() => {
      loadNotifications();
      loadMaintenanceAlert();
    }, pollingInterval);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollingInterval]);

  // export-started 이벤트 수신 → 빠른 폴링(5초) 전환
  useEffect(() => {
    const handleExportStarted = () => {
      // 이벤트 발생 시점의 최신 unreadCount를 ref에서 읽음
      prevUnreadCountRef.current = unreadCountRef.current;
      setPollingInterval(5000);

      // 3분 후 자동 복귀
      if (window._exportPollingTimeout) clearTimeout(window._exportPollingTimeout);
      window._exportPollingTimeout = setTimeout(() => {
        setPollingInterval(300000);
        window._exportPollingTimeout = null;
      }, 180000);
    };

    window.addEventListener('export-started', handleExportStarted);
    return () => window.removeEventListener('export-started', handleExportStarted);
  }, []); // 마운트 시 한 번만 등록

  // 빠른 폴링 중 새 알림 감지 → 즉시 복귀
  useEffect(() => {
    if (pollingInterval === 5000 && unreadCount > prevUnreadCountRef.current) {
      if (window._exportPollingTimeout) {
        clearTimeout(window._exportPollingTimeout);
        window._exportPollingTimeout = null;
      }
      setPollingInterval(300000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadCount]);

  // 점검 진행중 배너가 활성화될 때 body 상단 여백 추가
  useEffect(() => {
    const hasActiveBanner =
      maintenanceAlert &&
      maintenanceAlert.activeFlg &&
      !localStorage.getItem(`system_notification_${maintenanceAlert.id}_dismissed`);

    document.body.style.paddingTop = hasActiveBanner ? '80px' : '0px';
    document.body.style.transition = 'padding-top 0.3s ease';

    return () => {
      document.body.style.paddingTop = '0px';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maintenanceAlert?.id, maintenanceAlert?.activeFlg]);

  return (
    <>
      {/* 점검 진행중 배너 (type='maintenance', start_ts <= now <= end_ts 인 경우에만) */}
      {maintenanceAlert && maintenanceAlert.activeFlg && (
        <Alert
          severity="warning"
          icon={<Build />}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1300,
            borderRadius: 0,
            minHeight: '80px',
            display: 'flex',
            alignItems: 'center',
            '& .MuiAlert-message': { width: '100%', textAlign: 'center', py: 1 },
            '& .MuiAlert-action': { alignItems: 'flex-start', mr: 1, mt: 1 }
          }}
          action={
            <IconButton
              size="small"
              onClick={() => {
                localStorage.setItem(`system_notification_${maintenanceAlert.id}_dismissed`, new Date().toISOString());
                setMaintenanceAlert(prev => ({ ...prev, activeFlg: false }));
              }}
              sx={{ color: 'inherit' }}
            >
              <Close fontSize="small" />
            </IconButton>
          }
        >
          <Box sx={{ width: '100%', textAlign: 'center' }}>
            <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              {t(maintenanceAlert.displayTitle)}
            </Typography>
            <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
              <strong>{t('NOTI_MaintenanceScheduled')}:</strong>{' '}
              {formatMaintenanceDateTime(maintenanceAlert.startTs)} ~ {formatMaintenanceDateTime(maintenanceAlert.endTs)}
            </Typography>
            <Typography variant="body2" component="div" sx={{ fontSize: FS.H1, lineHeight: 1.4 }}>
              {t(maintenanceAlert.displayMessage)}
            </Typography>
          </Box>
        </Alert>
      )}

      {/* 알림 벨 아이콘 */}
      <IconButton
        color="inherit"
        size="small"
        onClick={handleClick}
        sx={{ mx: 1 }}
        title={t('NOTI_UnreadNotifications', { count: unreadCount })}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          showZero={false}
          max={99}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: FS.SUB,
              minWidth: '18px',
              height: '18px',
              backgroundColor: '#f44336',
              color: 'white'
            }
          }}
        >
          {unreadCount > 0 ? <NotificationsActive /> : <NotificationsOutlined />}
        </Badge>
      </IconButton>

      {/* 알림 팝오버 */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: showAllNotifications ? '700px' : '500px',
            display: 'flex',
            flexDirection: 'column',
            transition: 'max-height 0.3s ease'
          }
        }}
      >
        {/* 헤더 */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {t('NOTI_Notifications')} ({notifications.length})
            </Typography>
            {unreadCount > 0 && (
              <Button size="small" onClick={handleMarkAllAsRead}>
                {t('NOTI_MarkAllAsRead')}
              </Button>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {t('NOTI_UnreadNotifications', { count: unreadCount })}
          </Typography>
        </Box>

        {/* 알림 리스트 */}
        <Box sx={{
          flex: 1,
          overflow: 'auto',
          maxHeight: showAllNotifications ? '500px' : '350px',
          transition: 'max-height 0.3s ease'
        }}>
          {!Array.isArray(notifications) || notifications.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {t('NOTI_NoNotificationsMessage')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('NOTI_NotificationsWillAppearHere')}
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {displayNotifications.map((notification, index) => (
                <React.Fragment key={notification.id}>
                  <ListItem
                    sx={{
                      backgroundColor: notification.readFlg ? 'transparent' : 'action.hover',
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'action.selected' }
                    }}
                    onClick={() => {
                      if (!notification.readFlg) {
                        handleMarkAsRead(notification.id);
                      }
                      /*
                       * 안건 알림이면 그 안건을 연다.
                       *
                       * 두 가지를 같이 쏜다 — 화면을 탭으로 열라는 신호(plannel:openMenu)와
                       * 어느 안건인지(requestAgenda). 화면이 이미 열려 있으면 후자만으로 바로
                       * 열리고, 닫혀 있으면 열릴 때 그 값을 집어간다.
                       */
                      const agendaId = agendaIdOf(notification);
                      if (agendaId) {
                        requestAgenda(agendaId);
                        window.dispatchEvent(new CustomEvent('plannel:openMenu', { detail: { key: MEETING_AGENDA_MENU_KEY } }));
                        handleClose();
                      }
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'transparent' }}>
                        {getNotificationIcon(notification.type, notification.title)}
                      </Avatar>
                    </ListItemAvatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* 제목 */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: notification.readFlg ? 'normal' : 'bold', flex: 1, mr: 1 }}>
                          {t(notification.title)}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {!notification.readFlg && (
                            <Chip label={t('NOTI_NewNotification')} size="small" color="primary" sx={{ fontSize: FS.BODY, height: 20 }} />
                          )}
                          <IconButton
                            size="small"
                            onClick={(event) => handleDeleteNotification(notification.id, event)}
                            sx={{
                              color: 'text.secondary',
                              opacity: 0.7,
                              '&:hover': { opacity: 1, color: 'error.main' }
                            }}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>

                      {/* 본문 */}
                      <Box>
                        {/* 다운로드 링크가 있는 info 타입 알림 */}
                        {notification.type === 'info' && notification.msgTxt && (notification.msgTxt.startsWith('/data/export') || notification.msgTxt.startsWith('http')) ? (
                          <>
                            <Typography variant="body2" sx={{ mb: 0.5, display: 'block' }}>
                              {(() => {
                                const desc = notification.description || notification.descTxt || '';
                                const parts = desc.split('|||');
                                if (parts[0] === 'fileDownloadReadyDescription' && parts.length === 3) {
                                  const hours = Number(parts[2]);
                                  if (hours >= 24 && hours % 24 === 0) {
                                    return t('NOTI_FileDownloadReadyDescriptionDays', { fileName: parts[1], expiryDays: hours / 24 });
                                  }
                                  return t('NOTI_FileDownloadReadyDescription', { fileName: parts[1], expiryHours: parts[2] });
                                }
                                return desc;
                              })()}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              <a
                                href="#download"
                                onClick={async (e) => {
                                  e.preventDefault();
                                  const url = notification.msgTxt;
                                  try {
                                    if (url.startsWith('http')) {
                                      // S3 presigned URL: 인증 불필요, 직접 이동
                                      window.location.href = url;
                                    } else {
                                      // 로컬 /data/export/download: 인증 헤더 포함 Blob 다운로드
                                      const response = await restApi.get(url, { responseType: 'blob' });
                                      const contentDisposition = response.headers['content-disposition'] || '';
                                      let fileName = 'download.csv';
                                      const match = contentDisposition.match(/filename[^;=\n]*=["']?([^"'\n;]+)/);
                                      if (match) fileName = decodeURIComponent(match[1].replace(/\+/g, ' '));
                                      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
                                      const link = document.createElement('a');
                                      link.href = blobUrl;
                                      link.setAttribute('download', fileName);
                                      document.body.appendChild(link);
                                      link.click();
                                      link.remove();
                                      window.URL.revokeObjectURL(blobUrl);
                                    }
                                  } catch (err) {
                                    console.error('Download failed:', err);
                                  }
                                }}
                                style={{ color: '#1976d2', cursor: 'pointer' }}
                              >
                                {t('NOTI_FileDownloadLink')}
                              </a>
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" sx={{ mb: 0.5, display: 'block' }}>
                             {notification.msgTxt === 'NOTI_HotfixNoticeMessage'
                              ? t('NOTI_HotfixNoticeMessage', {
                                  date: formatMaintenanceDateShort(notification.startTs),
                                  startTime: formatMaintenanceTimeOnly(notification.startTs),
                                  endTime: formatMaintenanceTimeOnly(notification.endTs)
                                })
                              : notification.msgTxt ? t(notification.msgTxt) : notification.msgTxt
                            }
                          </Typography>
                        )}

                        {/* 눌러서 갈 수 있다는 것은 적어 주지 않으면 아무도 모른다 */}
                        {agendaIdOf(notification) && (
                          <Typography variant="caption" sx={{ color: 'primary.main', display: 'block', mb: 0.5 }}>
                            {t('NOTI_MeetingAgendaOpenHint')}
                          </Typography>
                        )}

                        {/* 시간/버전 정보: maintenance, maintenance-notice 타입 또는 releaseCompletedTitle 인 경우 */}
                        {(['maintenance', 'maintenance-notice'].includes(notification.type) ||
                          ['NOTI_ReleaseNoticeTitle', 'NOTI_ReleaseCompletedTitle'].includes(notification.title)) && (
                          <Typography variant="body2" sx={{
                            mb: 0.5,
                            display: 'block',
                            color: 'warning.main',
                            fontWeight: 'medium',
                            fontSize: FS.H2
                          }}>
                            {/* desc_txt(버전 정보)는 releaseNoticeTitle, releaseCompletedTitle 일 때만 표시 */}
                            {['NOTI_ReleaseNoticeTitle', 'NOTI_ReleaseCompletedTitle'].includes(notification.title) && notification.description && (
                              <>
                                <strong>{t('NOTI_ReleaseNotificationVersion')}:</strong>{' '}
                                {notification.description}
                                <br />
                              </>
                            )}
                            {/* releaseCompletedTitle은 시간 표시하지 않음 */}
                            {['maintenance', 'maintenance-notice'].includes(notification.type) &&
                              (notification.startTs || notification.endTs) && (
                                notification.title === 'NOTI_SalesActualArchivingTitle' ? (
                                  // 아카이빙 알림: end_ts(아카이빙 시작 시각)만 단독 표시
                                  <>
                                    <strong>{t('NOTI_ArchivingScheduled')}:</strong>{' '}
                                    {formatMaintenanceDateTime(notification.endTs)}
                                  </>
                                ) : (
                                  // 일반 점검: start ~ end 범위 표시
                                  <>
                                    <strong>{t(notification.title === 'NOTI_ReleaseNoticeTitle' ? 'NOTI_ReleaseScheduled' : 'NOTI_MaintenanceScheduled')}:</strong>{' '}
                                    {formatMaintenanceDateTime(notification.startTs)} ~ {formatMaintenanceDateTime(notification.endTs)}
                                  </>
                                )
                              )}
                          </Typography>
                        )}

                        {/* 릴리즈 노트 안내 문구: releaseNoticeTitle, releaseCompletedTitle 일 때만 표시 */}
                        {['NOTI_ReleaseNoticeTitle', 'NOTI_ReleaseCompletedTitle'].includes(notification.title) && (
                          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontStyle: 'italic' }}>
                            {t('NOTI_ReleaseNoticeReleaseNote')}
                          </Typography>
                        )}

                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(
                            notification.createdTs || notification.createdAt,
                            notification.type,
                            notification.startTs
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  </ListItem>
                  {index < displayNotifications.length - 1 && <Divider component="li" />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>

        {/* 더보기 버튼 */}
        {hasMoreNotifications && (
          <Box sx={{
            p: 1,
            textAlign: 'center',
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor: 'background.default',
            flexShrink: 0
          }}>
            <Button
              size="small"
              variant="text"
              color="primary"
              onClick={() => setShowAllNotifications(!showAllNotifications)}
              sx={{ fontSize: FS.TITLE, textTransform: 'none', fontWeight: 500, py: 0.5 }}
            >
              {showAllNotifications
                ? t('NOTI_ShowLess', { count: notifications.length - displayLimit })
                : t('NOTI_ShowMore', { count: notifications.length - displayLimit })}
            </Button>
          </Box>
        )}

        {/* 푸터 */}
        <Box sx={{
          p: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          textAlign: 'center',
          backgroundColor: 'grey.50',
          flexShrink: 0
        }}>
          <Button
            size="small"
            onClick={handleClose}
            variant="outlined"
            sx={{
              color: 'text.secondary',
              borderColor: 'divider',
              fontSize: FS.TITLE,
              px: 3,
              '&:hover': { backgroundColor: 'action.hover', borderColor: 'text.secondary' }
            }}
          >
            {t('close')}
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell;
